import { Send } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { RefObject } from "react";
import { Button } from "@/components/ui/button";

interface CanvasPendingCommentProps {
	/** Canvas-space position of the comment being written, or null when closed. */
	pendingComment: { x: number; y: number } | null;
	zoom: number;
	commentInput: string;
	onCommentInputChange: (value: string) => void;
	commentInputRef: RefObject<HTMLInputElement | null>;
	onSubmit: () => void;
	onCancel: () => void;
}

/** New-comment input that pops up where the user clicked in comment mode. */
export function CanvasPendingComment({
	pendingComment,
	zoom,
	commentInput,
	onCommentInputChange,
	commentInputRef,
	onSubmit,
	onCancel,
}: CanvasPendingCommentProps) {
	return (
		<AnimatePresence>
			{pendingComment && (
				<motion.div
					initial={{ opacity: 0, y: 6, scale: 0.9 }}
					animate={{ opacity: 1, y: 0, scale: 1 }}
					exit={{ opacity: 0, y: 6, scale: 0.9 }}
					transition={{ duration: 0.18, ease: "easeOut" }}
					className="absolute z-50"
					style={{
						left: pendingComment.x,
						top: pendingComment.y,
						transform: `scale(${1 / zoom})`,
						transformOrigin: "top left",
					}}
					onMouseDown={(e) => e.stopPropagation()}
				>
					<div className="bg-bg-surface border border-border-subtle rounded-xl shadow-popover min-w-[380px] overflow-hidden">
						<div className="flex items-center gap-2 px-3 py-1.5 border-b border-border-subtle">
							<div className="w-6 h-6 rounded-full bg-brand-400 flex items-center justify-center text-text-on-brand text-2xs font-bold flex-shrink-0">
								Y
							</div>
							<span className="text-xs font-medium text-text-secondary">
								You
							</span>
						</div>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								onSubmit();
							}}
							className="flex items-center gap-2 rounded-xl px-3 py-2.5 focus-within:ring-2 focus-within:ring-inset focus-within:ring-focus-ring"
						>
							<input
								ref={commentInputRef}
								type="text"
								value={commentInput}
								onChange={(e) => onCommentInputChange(e.target.value)}
								placeholder="Add a comment…"
								className="flex-1 bg-transparent text-text-primary placeholder:text-text-tertiary text-sm focus:outline-none"
								onKeyDown={(e) => {
									if (e.key === "Escape") {
										onCancel();
									}
								}}
							/>
							<Button
								type="submit"
								variant="secondary"
								size="icon-xs"
								disabled={!commentInput.trim()}
								className="flex-shrink-0"
							>
								<Send size={13} strokeWidth={2.5} />
							</Button>
						</form>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
